<script setup>
import { ref, watch, nextTick } from 'vue'

import { VuePDF } from '@tato30/vue-pdf'

import { usePdfLoader } from './composables/usePdfLoader'
import { usePdfZoom } from './composables/usePdfZoom'
import { usePdfNavigation } from './composables/usePdfNavigation'
import { usePdfScrollTracker } from './composables/usePdfScrollTracker'
import { usePdfDownload } from './composables/usePdfDownload'

import './styles/LeitorPdf.css'

const props = defineProps({
  url: String,
  token: String
})

const emit = defineEmits(['mounted'])

const src = {
  url: props.url,
  withCredentials: false,
  ...(props.token ? { httpHeaders: { Authorization: 'Bearer ' + props.token } } : {}),
  verbosity: 0
}

const scrollToPage = (n) => {
  const el = pagesContainer.value?.querySelector(`[data-page="${n}"]`)
  if (!el) return

  const top =
    el.getBoundingClientRect().top -
    pagesContainer.value.getBoundingClientRect().top +
    pagesContainer.value.scrollTop -
    20

  // Ativar flag de navegação para evitar conflito com trackScroll
  isNavigating.value = true
  pagesContainer.value.scrollTo({ top, behavior: 'smooth' })

  // Remover flag após animação do scroll
  setTimeout(() => {
    isNavigating.value = false
  }, 600)
}

const { pdf, pages, loading, errorMessage, errorMessageVisible } = usePdfLoader(src, emit)

const { scale, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut, scalePercentage } = usePdfZoom()

const { currentPage, nextPage, previousPage, canNext, canPrev } =  usePdfNavigation(pages, scrollToPage)

const { baixarArquivo } = usePdfDownload()

const isNavigating = ref(false)
const pagesContainer = ref(null)
const pagesPDF = ref(null)

const { updatePositions, trackScroll } = usePdfScrollTracker(pagesContainer, pages)

const handleScroll = () => {
  if (!isNavigating.value) {
    trackScroll(currentPage)
  }
}

// Atualizar posições das páginas quando o PDF for carregado
watch([pdf, pages], async () => {
  if (pdf.value && pages.value > 0) {
    await nextTick()
    updatePositions()
  }
})
</script>

<template>
  <!-- Barra de controles flutuante -->
  <div v-if="pdf && !errorMessageVisible" class="pdf-toolbar">
    <div class="pdf-toolbar-container">

      <!-- Controles de Zoom -->
      <div class="toolbar-group">
        <Button icon="pi pi-search-minus text-xs" text rounded size="small" style="height: 34px; width: 34px;" severity="contrast"
          :disabled="!canZoomOut" @click="zoomOut" v-tooltip.top="'Reduzir zoom (mín. 50%)'" class="toolbar-btn" />
        <Button :label="scalePercentage + '%'" text rounded size="small" style="height: 34px; padding: 0;" severity="contrast"
          @click="resetZoom" v-tooltip.top="'Clique para resetar para 100%'" class="toolbar-btn zoom-label text-xs" />
        <Button icon="pi pi-search-plus text-xs" text rounded size="small" style="height: 34px; width: 34px;" severity="contrast"
          :disabled="!canZoomIn" @click="zoomIn" v-tooltip.top="'Aumentar zoom (máx. 300%)'" class="toolbar-btn" />
      </div>

      <!-- Divisor -->
      <div class="toolbar-divider"></div>

      <!-- Controles de Navegação -->
      <div class="toolbar-group hidden md:flex">
        <Button icon="pi pi-chevron-up text-xs" text rounded size="small" severity="contrast" style="height: 34px; width: 34px;"
          :disabled="!canPrev" @click="previousPage" v-tooltip.top="'Página anterior'"
          class="toolbar-btn" />
        <span class="page-indicator text-xs">{{ currentPage }} / {{ pages }}</span>
        <Button icon="pi pi-chevron-down text-xs" text rounded severity="contrast" style="height: 34px; width: 34px;" size="small"
          :disabled="!canNext" @click="nextPage" v-tooltip.top="'Próxima página'" class="toolbar-btn" />
      </div>

      <!-- Divisor -->
      <div class="toolbar-divider"></div>

      <!-- Botão de Download -->
      <div class="toolbar-group">
        <Button icon="pi pi-download text-xs" text size="small" style="height: 34px; width: 34px;" rounded severity="contrast"
          @click="() => baixarArquivo(props.url, props.token)" v-tooltip.top="'Baixar PDF'" class="toolbar-btn" />
      </div>
    </div>
  </div>

  <!-- Container das páginas -->
  <div class="pdf-container" ref="pagesContainer" @scroll="handleScroll">
    <div class="pdf-pages-wrapper" ref="pagesPDF">

      <!-- Loading message -->
      <div v-if="loading && !errorMessageVisible"
        class="p-4 text-blue-600 flex flex-row justify-content-center font-medium bg-blue-100 border border-blue-300 border-round-lg">
        <div class="flex flex-row gap-3 w-max align-items-center">
          <i class="pi pi-spin pi-spinner"></i>
          <div class="flex flex-column">
            <p class="m-0 text-sm font-semibold mb-1">
              Carregando Arquivo...
            </p>
            <p class="m-0 text-xs font-normal">Por favor, aguarde enquanto o arquivo é carregado.</p>
            <p class="m-0 text-xs font-normal">Se o carregamento demorar muito, verifique sua conexão com a internet.
            </p>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div v-if="errorMessageVisible"
        class="p-4 text-red-600 flex flex-row justify-content-center font-medium bg-red-100 border border-red-300 border-round-lg">
        <div class="flex flex-row gap-3 w-max align-items-center">
          <i class="pi pi-exclamation-circle"></i>
          <div class="flex flex-column">
            <p class="m-0 text-sm font-semibold mb-1">
              {{ errorMessage || 'Documento não encontrado' }}
            </p>
            <p class="m-0 text-xs font-normal">Ocorreu um erro ao carregar o PDF. Verifique sua conexão com a internet
              ou tente novamente
              mais tarde.</p>
          </div>
        </div>
      </div>

      <!-- PDF pages -->
      <div v-if="!loading && !errorMessageVisible" class="pdf-pages">
        <div v-for="pageNumber in pages" :key="pageNumber" :data-page="pageNumber" class="pdf-page"
          :class="{ 'pdf-page-active': pageNumber === currentPage }">
          <VuePDF :pdf="pdf" :scale="scale" :page="pageNumber" :text-layer="true" class="pdf-canvas" />
          <div class="page-number-badge">{{ pageNumber }}</div>
        </div>
      </div>
    </div>
  </div>
</template>